// src/householdService.js
import {
  doc, getDoc, setDoc, updateDoc, deleteDoc,
  collection, addDoc, query, where, getDocs,
  arrayUnion, arrayRemove, serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";

// ── Household CRUD ────────────────────────────────────────────────────────────

export async function createHousehold(user, name) {
  const ref = doc(collection(db, "households"));
  const household = {
    id: ref.id,
    name,
    ownerId: user.uid,
    memberIds: [user.uid],
    members: [{
      uid: user.uid,
      displayName: user.displayName || user.email.split("@")[0],
      email: user.email,
      photoURL: user.photoURL || null,
      role: "owner",
      joinedAt: new Date().toISOString(),
    }],
    createdAt: serverTimestamp(),
  };
  await setDoc(ref, household);

  // Save householdId on user profile
  await setDoc(doc(db, "users", user.uid), {
    householdId: ref.id,
    displayName: user.displayName || user.email.split("@")[0],
    email: user.email,
    photoURL: user.photoURL || null,
  }, { merge: true });

  return { ...household, id: ref.id };
}

export async function getHousehold(householdId) {
  const snap = await getDoc(doc(db, "households", householdId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getUserHousehold(uid) {
  const userSnap = await getDoc(doc(db, "users", uid));
  if (!userSnap.exists()) return null;
  const { householdId } = userSnap.data();
  if (!householdId) return null;
  return getHousehold(householdId);
}

export async function updateHouseholdName(householdId, name) {
  await updateDoc(doc(db, "households", householdId), { name });
}

export async function removeMember(householdId, memberUid, memberData) {
  await updateDoc(doc(db, "households", householdId), {
    memberIds: arrayRemove(memberUid),
    members: arrayRemove(memberData),
  });
  // Clear householdId from removed user's profile
  await setDoc(doc(db, "users", memberUid), { householdId: null }, { merge: true });
}

export async function leaveHousehold(householdId, user) {
  const household = await getHousehold(householdId);
  if (!household) return;
  const memberData = household.members.find(m => m.uid === user.uid);
  if (memberData) {
    await updateDoc(doc(db, "households", householdId), {
      memberIds: arrayRemove(user.uid),
      members: arrayRemove(memberData),
    });
  }
  await setDoc(doc(db, "users", user.uid), { householdId: null }, { merge: true });
}

// ── Invites ───────────────────────────────────────────────────────────────────

function generateToken() {
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
}

export async function createInvite(householdId, householdName, inviterName, inviteeEmail) {
  // Check if there's already a pending invite for this email
  const q = query(
    collection(db, "invites"),
    where("householdId", "==", householdId),
    where("inviteeEmail", "==", inviteeEmail.toLowerCase()),
    where("status", "==", "pending")
  );
  const existing = await getDocs(q);
  if (!existing.empty) {
    // Return existing invite token
    return existing.docs[0].data().token;
  }

  const token = generateToken();
  await addDoc(collection(db, "invites"), {
    householdId,
    householdName,
    inviterName,
    inviteeEmail: inviteeEmail.toLowerCase(),
    token,
    status: "pending",
    createdAt: serverTimestamp(),
  });


  return token;
}

export async function getInviteByToken(token) {
  const q = query(collection(db, "invites"), where("token", "==", token));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}

export async function acceptInvite(invite, user) {
  // Add user to household
  await updateDoc(doc(db, "households", invite.householdId), {
    memberIds: arrayUnion(user.uid),
    members: arrayUnion({
      uid: user.uid,
      displayName: user.displayName || user.email.split("@")[0],
      email: user.email,
      photoURL: user.photoURL || null,
      role: "member",
      joinedAt: new Date().toISOString(),
    }),
  });

  // Save householdId on user profile
  await setDoc(doc(db, "users", user.uid), {
    householdId: invite.householdId,
    displayName: user.displayName || user.email.split("@")[0],
    email: user.email,
    photoURL: user.photoURL || null,
  }, { merge: true });

  // Mark invite as accepted
  await updateDoc(doc(db, "invites", invite.id), { status: "accepted" });
}

export async function getPendingInvites(householdId) {
  const q = query(
    collection(db, "invites"),
    where("householdId", "==", householdId),
    where("status", "==", "pending")
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function deleteInvite(inviteId) {
  await deleteDoc(doc(db, "invites", inviteId));
}

// Get invites sent TO a specific email address (for in-app notifications)
export async function getInvitesForEmail(email) {
  const q = query(
    collection(db, "invites"),
    where("inviteeEmail", "==", email.toLowerCase()),
    where("status", "==", "pending")
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Decline an invite
export async function declineInvite(inviteId) {
  await updateDoc(doc(db, "invites", inviteId), { status: "declined" });
}
